import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@floraltech/ui';

interface RigaParsed {
  numero_linea: number;
  descrizione: string | null;
  quantita: number | null;
  unita_misura: string | null;
  prezzo_unitario: number | null;
  aliquota_iva: number | null;
  natura_iva: string | null;
  importo_riga: number | null;
}

interface XmlData {
  tipo_documento_codice: string;
  data: string;
  numero: string;
  cedente_denominazione: string | null;
  cedente_piva: string | null;
  cedente_cf: string | null;
  cessionario_denominazione: string | null;
  cessionario_piva: string | null;
  cessionario_cf: string | null;
  importo_imponibile: number | null;
  importo_iva: number | null;
  importo_totale: number | null;
  righe: RigaParsed[];
  condizioni_pagamento: string | null;
  modalita_pagamento: string | null;
  data_scadenza: string | null;
  iban_pagamento: string | null;
  importo_bollo: number | null;
  tipo_ritenuta: string | null;
  importo_ritenuta: number | null;
  aliquota_ritenuta: number | null;
  causale_pagamento: string | null;
}

/**
 * Le descrizioni dei codici di pagamento NON stanno piu' qui.
 *
 * C'era una mappa scritta a mano con 8 codici su 23, e due etichette non
 * ufficiali ("Carta di credito" invece di "carta di pagamento", "SEPA DD"
 * abbreviata): tutti gli altri codici cadevano nel fallback e si leggevano
 * nudi. Ora il dominio vive a database (migration 0026, popolata dalle
 * Specifiche FatturaPA 1.4) ed e' servito da
 * /api/v1/fatture/lookups/fatturapa-pagamento, lo stesso endpoint che alimenta
 * la tendina del form: una sola fonte, sempre allineata alla norma.
 */
interface DominioPagamento {
  codice: string;
  descrizione: string;
}

interface Props {
  fatturaId: string;
  year: number;
}

const XmlViewer: React.FC<Props> = ({ fatturaId, year }) => {
  const [data, setData] = useState<XmlData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [MP, setMP] = useState<Record<string, string>>({});
  const [TP, setTP] = useState<Record<string, string>>({});

  // Il dominio e' chiuso e non cambia mai durante la sessione: una fetch sola.
  // Se fallisce, i codici restano nudi come prima: e' un degrado accettabile
  // su un pannello di sola lettura, non vale un messaggio di errore.
  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch('/api/v1/fatture/lookups/fatturapa-pagamento', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((d) => {
        if (!d) return;
        const toMap = (voci: DominioPagamento[]) =>
          Object.fromEntries(voci.map((v) => [v.codice, v.descrizione]));
        setMP(toMap(d.modalita ?? []));
        setTP(toMap(d.condizioni ?? []));
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`/api/v1/fatture/${fatturaId}/xml?year=${year}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => {
        if (!res.ok) throw new Error(res.status === 404 ? 'XML non disponibile' : 'Errore caricamento');
        return res.json();
      })
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [fatturaId, year]);

  if (loading) return <div className="text-muted-foreground p-4">Caricamento XML...</div>;
  if (error) return <div className="text-destructive p-4">{error}</div>;
  if (!data) return null;

  const fmt = (v: number | null) =>
    v != null ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v) : '-';

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle className="text-base">Dati Generali</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div><span className="text-muted-foreground">Tipo:</span> {data.tipo_documento_codice}</div>
            <div><span className="text-muted-foreground">Numero:</span> {data.numero}</div>
            <div><span className="text-muted-foreground">Data:</span> {data.data}</div>
            <div><span className="text-muted-foreground">Totale:</span> {fmt(data.importo_totale)}</div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Cedente / Prestatore</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div><span className="text-muted-foreground">Denominazione:</span> {data.cedente_denominazione || '-'}</div>
            <div><span className="text-muted-foreground">P.IVA:</span> {data.cedente_piva || '-'}</div>
            <div><span className="text-muted-foreground">C.F.:</span> {data.cedente_cf || '-'}</div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Cessionario / Committente</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div><span className="text-muted-foreground">Denominazione:</span> {data.cessionario_denominazione || '-'}</div>
            <div><span className="text-muted-foreground">P.IVA:</span> {data.cessionario_piva || '-'}</div>
            <div><span className="text-muted-foreground">C.F.:</span> {data.cessionario_cf || '-'}</div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Dettaglio Righe ({data.righe.length})</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2 pr-2">#</th>
                  <th className="py-2 pr-2">Descrizione</th>
                  <th className="py-2 pr-2 text-right">Qtà</th>
                  <th className="py-2 pr-2 text-right">Prezzo</th>
                  <th className="py-2 pr-2 text-right">IVA %</th>
                  <th className="py-2 text-right">Importo</th>
                </tr>
              </thead>
              <tbody>
                {data.righe.map(r => (
                  <tr key={r.numero_linea} className="border-b">
                    <td className="py-2 pr-2">{r.numero_linea}</td>
                    <td className="py-2 pr-2 max-w-xs truncate">{r.descrizione || '-'}</td>
                    <td className="py-2 pr-2 text-right">{r.quantita ?? '-'} {r.unita_misura || ''}</td>
                    <td className="py-2 pr-2 text-right">{fmt(r.prezzo_unitario)}</td>
                    <td className="py-2 pr-2 text-right">{r.aliquota_iva != null ? `${r.aliquota_iva}%` : r.natura_iva || '-'}</td>
                    <td className="py-2 text-right font-medium">{fmt(r.importo_riga)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-medium"><td colSpan={5} className="py-2 text-right">Imponibile:</td><td className="py-2 text-right">{fmt(data.importo_imponibile)}</td></tr>
                <tr className="font-medium"><td colSpan={5} className="py-2 text-right">IVA:</td><td className="py-2 text-right">{fmt(data.importo_iva)}</td></tr>
                <tr className="font-semibold text-base"><td colSpan={5} className="py-2 text-right">Totale:</td><td className="py-2 text-right">{fmt(data.importo_totale)}</td></tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {(data.condizioni_pagamento || data.modalita_pagamento || data.data_scadenza) && (
        <Card>
          <CardHeader><CardTitle className="text-base">Dati Pagamento</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              {data.condizioni_pagamento && <div><span className="text-muted-foreground">Condizioni:</span> {TP[data.condizioni_pagamento] || data.condizioni_pagamento}</div>}
              {data.modalita_pagamento && <div><span className="text-muted-foreground">Modalità:</span> {MP[data.modalita_pagamento] || data.modalita_pagamento}</div>}
              {data.data_scadenza && <div><span className="text-muted-foreground">Scadenza:</span> {data.data_scadenza}</div>}
              {data.iban_pagamento && <div><span className="text-muted-foreground">IBAN:</span> {data.iban_pagamento}</div>}
            </div>
          </CardContent>
        </Card>
      )}

      {(data.importo_bollo || data.importo_ritenuta) && (
        <Card>
          <CardHeader><CardTitle className="text-base">Bollo e Ritenuta</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              {data.importo_bollo && <div><span className="text-muted-foreground">Bollo virtuale:</span> {fmt(data.importo_bollo)}</div>}
              {data.importo_ritenuta && <div><span className="text-muted-foreground">Ritenuta:</span> {fmt(data.importo_ritenuta)} ({data.aliquota_ritenuta}%)</div>}
              {data.tipo_ritenuta && <div><span className="text-muted-foreground">Tipo:</span> {data.tipo_ritenuta === 'RT01' ? 'Persone fisiche' : 'Persone giuridiche'}</div>}
              {data.causale_pagamento && <div><span className="text-muted-foreground">Causale:</span> {data.causale_pagamento}</div>}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default XmlViewer;
